import type { FC } from 'react'
import { Container, Divider, Link, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Link as RouterLink } from 'react-router-dom'
import {
  backIconSx,
  backLinkSx,
  containerSx,
  dividerSx,
  inlineLinkSx,
  sectionSx,
  sectionTitleSx,
  titleSx,
} from '../styles'

export const FaqPage: FC = () => {
  return (
    <Container maxWidth="md" sx={containerSx}>
      <Link component={RouterLink} to="/docs" sx={backLinkSx}>
        <ArrowBackIcon sx={backIconSx} />
        Back to Docs
      </Link>

      <Typography variant="h5" sx={titleSx}>
        FAQ
      </Typography>

      {/* Q1 */}
      <Typography variant="h6" sx={sectionTitleSx}>
        Are you an AI wrapper service?
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={sectionSx}>
        No. We don&apos;t provide Claude Code and we don&apos;t proxy your interactions with it.
        Claude Code runs locally on your machine and communicates directly with Anthropic&apos;s API
        — we are not in that path.{' '}
        <Link component={RouterLink} to="/docs/daemon-setup#what-is-the-daemon" sx={inlineLinkSx}>
          The daemon
        </Link>{' '}
        is a side-channel observer: it captures session events through Claude Code&apos;s hook
        system after they occur, encrypts them, and streams them to the dashboard. We never see your
        Anthropic credentials — whether you authenticate with an API key, a login, or any other
        method — we never intercept your prompts, and we never sit between you and the model.
      </Typography>

      <Divider sx={dividerSx} />

      {/* Q2 */}
      <Typography variant="h6" id="how-it-works" sx={sectionTitleSx}>
        How does this work?
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={sectionSx}>
        Claude Code supports lifecycle hooks — shell commands triggered on session events (start,
        stop, tool use, etc.). The daemon registers hook entries in your Claude Code settings file (
        <code>~/.claude/settings.json</code>). When Claude Code fires a hook, the daemon captures
        the event, encrypts it, and streams it to the backend. The backend relays the ciphertext to
        the dashboard, which decrypts and displays it. See the{' '}
        <Link
          href="https://code.claude.com/docs/en/hooks"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Claude Code hooks documentation (opens in new tab)"
        >
          Claude Code hooks documentation
        </Link>{' '}
        for the full hook specification, the{' '}
        <Link component={RouterLink} to="/docs/security" sx={inlineLinkSx}>
          security model
        </Link>{' '}
        for encryption details, and the{' '}
        <Link component={RouterLink} to="/docs/daemon-setup" sx={inlineLinkSx}>
          daemon setup guide
        </Link>{' '}
        for installation.
      </Typography>

      <Divider sx={dividerSx} />

      {/* Q3 */}
      <Typography variant="h6" id="why-tmux" sx={sectionTitleSx}>
        Why does the daemon need tmux?
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={sectionSx}>
        <Link
          href="https://github.com/tmux/tmux/wiki"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="tmux documentation (opens in new tab)"
        >
          tmux
        </Link>{' '}
        is the only reliable way to programmatically create terminal sessions and inject user input
        into them. The daemon uses tmux to launch Claude Code sessions, send commands, and manage
        session lifecycle (attach, detach, rename, stop). Without a terminal multiplexer, there is
        no portable mechanism to interact with an already-running terminal process.
      </Typography>

      <Divider sx={dividerSx} />

      {/* Q4 */}
      <Typography variant="h6" sx={sectionTitleSx}>
        Can the dashboard or backend inject malicious commands?
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={sectionSx}>
        No. The dashboard relays exactly what you type — your prompts, your tool approvals, your
        answers. The only non-user content the backend injects is short error recovery prompts:
        plain-text instructions telling Claude to retry after an API error or to re-present a
        dismissed tool permission dialog as a text-based option. These are the only two recovery
        types and both are simple instructional text. Everything else originates from you.
      </Typography>

      <Divider sx={dividerSx} />

      {/* Q5 */}
      <Typography variant="h6" sx={sectionTitleSx}>
        Can you read my data?
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={sectionSx}>
        No. All session data is end-to-end encrypted with{' '}
        <Link
          href="https://en.wikipedia.org/wiki/Galois/Counter_Mode"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="AES-256-GCM encryption explained (opens in new tab)"
        >
          AES-256-GCM
        </Link>
        . The daemon encrypts before sending. The backend stores and relays ciphertext — it has no
        encryption keys and cannot decrypt anything. Only your machines (running the daemon) and
        your dashboard (which loads the key ring on sign-in via manual upload) can read the data.
        The backend is a blind relay by design — even if it were compromised, an attacker would only
        obtain encrypted blobs, useless without your key ring. See the{' '}
        <Link component={RouterLink} to="/docs/security" sx={inlineLinkSx}>
          security model
        </Link>{' '}
        for the full encryption architecture.
      </Typography>

      <Divider sx={dividerSx} />

      {/* Q6 */}
      <Typography variant="h6" sx={sectionTitleSx}>
        Who can see my data?
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={sectionSx}>
        Only you. Each host (machine) is registered with a unique identifier and authenticated via
        API keys that you generate. The backend enforces ownership — requests are scoped to your
        user and your registered hosts. No other user can query, stream, or access your events. See
        the{' '}
        <Link component={RouterLink} to="/docs/onboarding" sx={inlineLinkSx}>
          onboarding
        </Link>{' '}
        guide for registration details.
      </Typography>

      <Divider sx={dividerSx} />

      {/* Q7 */}
      <Typography variant="h6" sx={sectionTitleSx}>
        What data leaves my machine?
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={sectionSx}>
        Session events — start, stop, tool use, prompts, agent responses, and activity state. All
        content is encrypted before it leaves: file paths, tool arguments, message text, everything.
        The only plaintext is routing metadata the backend needs to deliver messages: host ID,
        session ID, event type, and timestamps. See the{' '}
        <Link component={RouterLink} to="/docs/security" sx={inlineLinkSx}>
          security model
        </Link>{' '}
        for the full encryption scope.
      </Typography>

      <Divider sx={dividerSx} />

      {/* Q8 */}
      <Typography variant="h6" sx={sectionTitleSx}>
        Does the daemon slow down Claude Code?
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={sectionSx}>
        No. Hook processing is asynchronous — Claude Code fires the hook and continues immediately.
        It does not wait for the daemon to finish processing, encrypting, or streaming the event.
        There is zero performance impact on your coding sessions.
      </Typography>

      <Divider sx={dividerSx} />

      {/* Q9 */}
      <Typography variant="h6" sx={sectionTitleSx}>
        What happens if the daemon or backend goes down?
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={sectionSx}>
        Claude Code keeps working normally. It maintains its own local transcripts and session state
        independently — the daemon does not affect Claude Code operations in any way. If the daemon
        is unavailable, hooks fail silently and dashboard events are not captured, but your coding
        session continues uninterrupted. Events during downtime are lost — when the daemon
        reconnects, new events stream as usual.
      </Typography>

      <Divider sx={dividerSx} />

      {/* Q10 */}
      <Typography variant="h6" sx={sectionTitleSx}>
        Will setup break my existing Claude Code hooks?
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={sectionSx}>
        No. During setup, the daemon backs up your existing settings file (
        <code>~/.claude/settings.json</code>) with a timestamp. Any hook commands you previously
        configured are preserved alongside the new daemon entries. The daemon adds its own hook
        entries without removing or modifying yours. See the{' '}
        <Link component={RouterLink} to="/docs/daemon-setup" sx={inlineLinkSx}>
          daemon setup guide
        </Link>{' '}
        for the full installation process.
      </Typography>
    </Container>
  )
}
